// 슬롯 생성/수정 모달
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Grid,
  Stack,
  useToast,
  Select
} from '@chakra-ui/react';
import { useAuth } from 'contexts/AuthContext';
import {
  createWorkSchedule,
  updateWorkSchedule,
  deleteWorkSchedule,
  getLocations,
  getAllSemesters
} from 'utils/firestoreUtils';

function SlotCreationModal({ isOpen, onClose, editingSchedule, onSaved }) {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [semesters, setSemesters] = useState([]);

  const [formData, setFormData] = useState({
    semesterId: '',
    workDate: '',
    startTime: '',
    endTime: '',
    requiredHeadcount: 1,
    location: '',
    description: '',
    wageAmount: '10320'
  });

  useEffect(() => {
    loadLocations();
    loadSemesters();
  }, []);

  useEffect(() => {
    if (editingSchedule) {
      const isCopy = !editingSchedule.id;
      setFormData({
        semesterId: editingSchedule.semesterId || '',
        workDate: editingSchedule.workDate || '',
        startTime: editingSchedule.startTime,
        endTime: editingSchedule.endTime,
        requiredHeadcount: editingSchedule.requiredHeadcount,
        location: editingSchedule.location,
        description: isCopy ? `${editingSchedule.description || ''} (복사)`.trim() : (editingSchedule.description || ''),
        wageAmount: editingSchedule.wageAmount
      });
    } else {
      setFormData({
        semesterId: '',
        workDate: '',
        startTime: '',
        endTime: '',
        requiredHeadcount: 1,
        location: '',
        description: '',
        wageAmount: '10320'
      });
    }
  }, [editingSchedule, isOpen]);

  const loadLocations = async () => {
    try {
      const locs = await getLocations();
      setLocations(locs);
    } catch (error) {
      console.error('위치 조회 오류:', error);
    }
  };

  const loadSemesters = async () => {
    try {
      const sems = await getAllSemesters();
      setSemesters(sems);
    } catch (error) {
      console.error('학기 조회 오류:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.semesterId || !formData.workDate || !formData.startTime || !formData.endTime || !formData.location) {
      toast({
        title: '입력 오류',
        description: '학기를 포함한 모든 필수 항목을 입력해주세요.',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    try {
      setLoading(true);
      
      if (editingSchedule?.id) {
        // 수정
        await updateWorkSchedule(editingSchedule.id, formData);
        toast({
          title: '수정 완료',
          description: '스케줄이 수정되었습니다.',
          status: 'success',
          duration: 3000
        });
      } else {
        // 신규 생성 또는 복사
        await createWorkSchedule(formData, currentUser.uid);
        toast({
          title: editingSchedule ? '복사 완료' : '생성 완료',
          description: editingSchedule ? '스케줄이 복사되었습니다.' : '스케줄이 생성되었습니다.',
          status: 'success',
          duration: 3000
        });
      }
      
      onSaved();
    } catch (error) {
      console.error('저장 오류:', error);
      toast({
        title: '저장 실패',
        description: error.message || '스케줄 저장에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!editingSchedule) return;
    
    if (!window.confirm('정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      return;
    }

    try {
      setLoading(true);
      await deleteWorkSchedule(editingSchedule.id);
      
      toast({
        title: '삭제 완료',
        description: '스케줄이 삭제되었습니다.',
        status: 'success',
        duration: 3000
      });
      
      onSaved();
    } catch (error) {
      console.error('삭제 오류:', error);
      toast({
        title: '삭제 실패',
        description: '스케줄 삭제에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {editingSchedule?.id ? '스케줄 수정' : editingSchedule ? '스케줄 복사' : '새 스케줄 추가'}
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <Stack spacing={4}>
            <FormControl isRequired>
              <FormLabel>학기</FormLabel>
              <Select
                value={formData.semesterId}
                onChange={(e) => handleChange('semesterId', e.target.value)}
                placeholder="학기를 선택하세요"
              >
                {semesters.map(sem => (
                  <option key={sem.id} value={sem.id}>
                    {sem.semesterName} ({sem.startDate} ~ {sem.endDate})
                  </option>
                ))}
              </Select>
            </FormControl>

            <Grid templateColumns="repeat(3, 1fr)" gap={4}>
              <FormControl isRequired>
                <FormLabel>근무일</FormLabel>
                <Input
                  type="date"
                  value={formData.workDate}
                  onChange={(e) => handleChange('workDate', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>시작시간</FormLabel>
                <Input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => handleChange('startTime', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>종료시간</FormLabel>
                <Input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => handleChange('endTime', e.target.value)}
                />
              </FormControl>
            </Grid>

            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <FormControl isRequired>
                <FormLabel>근무지</FormLabel>
                <Select
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  placeholder="선택하세요"
                >
                  {locations.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>필요 인원</FormLabel>
                <NumberInput
                  value={formData.requiredHeadcount}
                  min={1}
                  onChange={(_, value) => handleChange('requiredHeadcount', value)}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </Grid>

            <FormControl isRequired>
              <FormLabel>시급 (원)</FormLabel>
              <NumberInput
                value={formData.wageAmount}
                min={0}
                step={10}
                onChange={(_, value) => handleChange('wageAmount', value)}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <FormControl>
              <FormLabel>업무 설명</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="업무 내용을 입력하세요"
                rows={3}
              />
            </FormControl>
          </Stack>
        </ModalBody>

        <ModalFooter>
          {editingSchedule?.id && (
            <Button
              colorScheme="red"
              mr="auto"
              onClick={handleDelete}
              isLoading={loading}
            >
              삭제
            </Button>
          )}
          <Button variant="ghost" mr={3} onClick={onClose}>
            취소
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={loading}
          >
            {editingSchedule?.id ? '수정' : editingSchedule ? '복사 생성' : '생성'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default SlotCreationModal;
